import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { LoaderCircle } from 'lucide-react';
import { InfoDot, type MetricInfo } from './InfoDot';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';

export function Button({
  variant = 'secondary',
  busy = false,
  className = '',
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; busy?: boolean }) {
  const base =
    'inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50';
  const styles: Record<Variant, string> = {
    primary: 'bg-navy text-white hover:bg-navy-deep',
    secondary: 'border border-line bg-white text-ink hover:bg-band',
    ghost: 'text-ink hover:bg-band',
    danger: 'bg-danger text-white hover:brightness-95',
  };
  return (
    <button type="button" className={`${base} ${styles[variant]} ${className}`} disabled={busy || rest.disabled} {...rest}>
      {busy && <LoaderCircle size={14} className="animate-spin" />}
      {children}
    </button>
  );
}

export function Card({ title, actions, info, children, className = '' }: { title?: string; actions?: ReactNode; info?: MetricInfo; children: ReactNode; className?: string }) {
  return (
    <section className={`rounded-lg border border-line bg-white ${className}`}>
      {(title || actions) && (
        <header className="flex items-center justify-between gap-3 border-b border-line px-4 py-2.5">
          {title && <h2 className="flex items-center gap-1.5 text-sm font-semibold text-navy"><span>{title}</span>{info && <InfoDot info={info} label={title} />}</h2>}
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className="px-4 py-3">{children}</div>
    </section>
  );
}

type Tone = 'neutral' | 'navy' | 'gold' | 'ok' | 'warn' | 'danger';

export function Chip({ tone = 'neutral', children, className = '', title }: { tone?: Tone; children: ReactNode; className?: string; title?: string }) {
  const tones: Record<Tone, string> = {
    neutral: 'bg-band text-ink border-line',
    navy: 'bg-navy-soft text-navy border-navy-soft',
    gold: 'bg-gold-soft text-warn border-gold-soft',
    ok: 'bg-ok-soft text-ok border-ok-soft',
    warn: 'bg-warn-soft text-warn border-warn-soft',
    danger: 'bg-danger-soft text-danger border-danger-soft',
  };
  return (
    <span title={title} className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${tones[tone]} ${className}`}>
      {children}
    </span>
  );
}

export function PageHeader({ title, description, actions }: { title: string; description?: string; actions?: ReactNode }) {
  return (
    <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="text-xl font-semibold text-navy">{title}</h1>
        {description && <p className="mt-1 max-w-3xl text-sm text-ink-muted">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

/** Spec 5.13: empty states explain what the page is for and offer the first action. */
export function EmptyState({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed border-line bg-white px-6 py-10 text-center">
      <h3 className="text-base font-semibold text-navy">{title}</h3>
      {children && <div className="mx-auto mt-2 max-w-xl text-sm text-ink-muted">{children}</div>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 text-sm text-ink-muted" role="status">
      <LoaderCircle size={16} className="animate-spin" /> {label}
    </div>
  );
}

export function ErrorNote({ message }: { message: string }) {
  return (
    <div role="alert" className="rounded-md border border-danger-soft bg-danger-soft px-3 py-2 text-sm text-danger">
      {message}
    </div>
  );
}

/** A small tab strip for switching between views of the same thing. */
export function Segmented<T extends string>({ value, onChange, options }: { value: T; onChange(v: T): void; options: [T, string][] }) {
  return (
    <div role="tablist" className="inline-flex rounded-md border border-line bg-white p-0.5 text-sm">
      {options.map(([v, label]) => (
        <button key={v} role="tab" aria-selected={value === v} type="button" onClick={() => onChange(v)} className={`rounded px-3 py-1 ${value === v ? 'bg-navy text-white' : 'text-ink hover:bg-band'}`}>
          {label}
        </button>
      ))}
    </div>
  );
}
